import React, { useEffect, useRef, useState } from 'react';

const WATCH_BASE = 'https://www.youtube.com/watch?v=';

/**
 * Click-to-load YouTube embed (WD-06).
 * No iframe until user clicks Play; iframe unmounts when the block leaves view.
 */
const YoutubeEmbed = ({
  videoId,
  title,
  heading,
  description,
  className = '',
  sectionClassName = 'py-16 bg-black',
  watchLabel = 'Watch on YouTube',
}) => {
  const containerRef = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const watchUrl = `${WATCH_BASE}${videoId}`;
  const thumbnailUrl = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  const embedUrl = `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;

  useEffect(() => {
    if (!isActive || !containerRef.current) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            setIsActive(false);
            setLoadError(false);
          }
        });
      },
      { threshold: 0 }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isActive]);

  const handleActivate = () => {
    setLoadError(false);
    setIsActive(true);
  };

  return (
    <section className={sectionClassName} ref={containerRef}>
      <div className={`container mx-auto px-4 ${className}`}>
        {heading && (
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-4">{heading}</h2>
        )}
        {description && (
          <p className="text-lg text-gray-300 text-center mb-12 max-w-3xl mx-auto">{description}</p>
        )}

        <div className="mx-auto relative px-4" style={{ maxWidth: '96rem' }}>
          <div className="relative rounded-lg overflow-hidden" style={{ paddingBottom: '56.25%', height: 0 }}>
            {isActive ? (
              <iframe
                src={embedUrl}
                title={title}
                className="absolute top-0 left-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                onError={() => setLoadError(true)}
              />
            ) : (
              <button
                type="button"
                onClick={handleActivate}
                className="absolute inset-0 w-full h-full group focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow focus-visible:ring-offset-2"
                aria-label={`Play ${title}`}
              >
                <img
                  src={thumbnailUrl}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <span className="absolute inset-0 bg-black/35 group-hover:bg-black/45 transition-colors motion-reduce:transition-none" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-red-600 text-white shadow-lg group-hover:bg-red-700 transition-colors motion-reduce:transition-none">
                    <span className="ml-1 text-2xl" aria-hidden="true">
                      ▶
                    </span>
                  </span>
                </span>
              </button>
            )}
          </div>

          <p className="text-center mt-4">
            <a
              href={watchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-gray-300 underline hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow"
            >
              {watchLabel}
            </a>
          </p>

          {loadError && (
            <p className="text-center text-gray-300 mt-2 text-sm">
              Video didn&apos;t load —{' '}
              <a href={watchUrl} target="_blank" rel="noopener noreferrer" className="underline">
                watch on YouTube
              </a>
              .
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

export default YoutubeEmbed;
